import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { api, ApiError, tanaan as paikallinenPaiva, type Poissaolo, type Poissaololaji } from "./api";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Henkilön poissaolot — sama data kuin resursoinnin poissaolo-
   näkymässä, mutta henkilön alla. Jussi kysyi: lomat löytyvät
   resursseista, mutta kätevintä on merkitä ne suoraan henkilölle.

   Arkaluonteinen laji (terveystieto) näkyy vain resursoinnista
   vastaaville rooleille — sama rajaus kuin API:ssa.
   ═══════════════════════════════════════════════════════════ */

// Riittävän leveä ikkuna: menneet ja tulevat lomat samaan listaan.
function ikkuna(): { alku: string; loppu: string } {
  const v = new Date().getFullYear();
  return { alku: `${v - 3}-01-01`, loppu: `${v + 2}-12-31` };
}

export default function HenkilonPoissaolot({
  henkiloId, nimi, saaMuokata,
}: {
  henkiloId: number;
  nimi: string;
  saaMuokata: boolean;
}) {
  const [rivit, setRivit] = useState<Poissaolo[] | null>(null);
  const [lajit, setLajit] = useState<Poissaololaji[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);

  // Kirjauslomake
  const [lajiId, setLajiId] = useState<number | "">("");
  const [alkaa, setAlkaa] = useState("");
  const [paattyy, setPaattyy] = useState("");
  const [huomiot, setHuomiot] = useState("");
  const [tallennetaan, setTallennetaan] = useState(false);

  const lataa = useCallback(async () => {
    const { alku, loppu } = ikkuna();
    try { setRivit(await api.poissaolot(alku, loppu, henkiloId)); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.haku_virhe")); setRivit([]); }
  }, [henkiloId]);

  useEffect(() => { void lataa(); }, [lataa]);
  useEffect(() => { api.poissaololajit().then(setLajit).catch(() => setLajit([])); }, []);

  async function kirjaa() {
    if (lajiId === "") { setVirhe(t("hpo.valitse_laji")); return; }
    if (!alkaa || !paattyy) { setVirhe(t("hpo.anna_paivat")); return; }
    if (paattyy < alkaa) { setVirhe(t("hpo.loppu_ennen")); return; }
    setTallennetaan(true); setVirhe(null);
    try {
      await api.merkitsePoissaolo({
        henkilo_id: henkiloId, poissaololaji_id: lajiId,
        alkaa, paattyy, ...(huomiot.trim() && { huomiot: huomiot.trim() }),
      });
      setLajiId(""); setAlkaa(""); setPaattyy(""); setHuomiot("");
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("hl.kirjaus_ei_onnistunut")); }
    finally { setTallennetaan(false); }
  }

  async function poista(id: number) {
    try { await api.poistaPoissaolo(id); await lataa(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe")); }
  }

  // Toni 15.10.2026 klo 19.30: paikallinen päivä yhteisestä apufunktiosta (UTC-päivä on Suomessa klo 00–03 eilinen).
  const tanaan = paikallinenPaiva();

  return (
    <div className="henkilopatevyys">
      <h3>{t("hpo.otsikko")}</h3>

      {rivit === null && <p className="dim">{t("ltar.ladataan")}</p>}
      {rivit && rivit.length === 0 && <p className="dim">{t("hpo.ei_kirjattuja")}</p>}

      {rivit && rivit.length > 0 && (
        <ul className="henkilopatevyys__lista">
          {rivit.map((r) => {
            const ohi = r.paattyy < tanaan;
            const kaynnissa = r.alkaa <= tanaan && r.paattyy >= tanaan;
            return (
              <li key={r.id} className="henkilopatevyys__rivi">
                <div className="henkilopatevyys__paa">
                  <span className="henkilopatevyys__nimi">{r.laji}</span>
                  <span className={"tilamerkki tilamerkki--" + (kaynnissa ? "varoitus" : ohi ? "ok" : "ok")}>
                    {kaynnissa ? t("hpo.kaynnissa") : ohi ? t("hpo.mennyt") : t("hpo.tuleva")}
                  </span>
                  <span className="dim mono henkilopatevyys__pvm">
                    {naytaPvm(r.alkaa)} – {naytaPvm(r.paattyy)}
                  </span>
                  {saaMuokata && (
                    <button type="button" className="henkilopatevyys__poista" title={t("hpo.poista")}
                            onClick={() => void poista(r.id)}>×</button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {saaMuokata && (
        <div className="henkilopatevyys__kirjaus">
          <select value={lajiId}
                  onChange={(e) => setLajiId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("hpo.lisaa")}</option>
            {lajit.map((l) => <option key={l.id} value={l.id}>{l.nimi}</option>)}
          </select>
          <label className="henkilopatevyys__kentta">
            {t("hpo.alkaa")}
            <input type="date" value={alkaa} onChange={(e) => setAlkaa(e.target.value)} />
          </label>
          <label className="henkilopatevyys__kentta">
            {t("hpo.paattyy")}
            <input type="date" value={paattyy} onChange={(e) => setPaattyy(e.target.value)} />
          </label>
          <input type="text" placeholder={t("hpo.huomiot")} value={huomiot} maxLength={500}
                 onChange={(e) => setHuomiot(e.target.value)} style={{ minWidth: 140 }} />
          <button type="button" className="nappi nappi--paa"
                  disabled={tallennetaan || lajiId === "" || !alkaa || !paattyy}
                  onClick={() => void kirjaa()}>
            {tallennetaan ? t("hl.kirjataan") : t("hpo.merkitse")}
          </button>
        </div>
      )}

      {virhe && <p className="huom" role="alert">{virhe}</p>}
      <p className="dim henkilopatevyys__vihje">
        {t("hpo.vihje", { nimi })}
      </p>
    </div>
  );
}
